/* NAI Signs — personalization field validation.
 * - Add to cart: Dawn's product form uses novalidate, so the submit is blocked here while
 *   the field is invalid (the input belongs to the product form via form="").
 * - Buy it now: the dynamic checkout button skips the form submit, so a click is blocked
 *   here until the field is valid.
 * - Shows the help/error message and a character counter. */
(() => {
  if (window.naiPersonalizationInit) return;
  window.naiPersonalizationInit = true;

  const SELECTOR = '[data-nai-personalization]';

  /* Note: form.id can't be used, Dawn's form has an input named "id". */
  const inputsForForm = (form) => {
    const formId = form && form.getAttribute('id');
    return formId ? document.querySelectorAll(`${SELECTOR}[form="${CSS.escape(formId)}"]`) : [];
  };

  /* Returns false (and shows the message) if a field of this form is invalid. */
  const validateForm = (form) => {
    for (const input of inputsForForm(form)) {
      if (!validate(input, true)) {
        showInvalid(input);
        return false;
      }
    }
    return true;
  };

  const parts = (input) => {
    const wrapper = input.closest('.nai-personalization');
    return {
      error: wrapper && wrapper.querySelector('.nai-personalization__error'),
      counter: wrapper && wrapper.querySelector('[data-nai-counter]'),
    };
  };

  const paymentButtonFor = (input) => {
    const formId = input.getAttribute('form');
    const form = formId && document.getElementById(formId);
    const productForm = form && form.closest('product-form');
    return productForm ? productForm.querySelector('.shopify-payment-button') : null;
  };

  const message = (input) => {
    const value = input.value.trim();
    const min = parseInt(input.dataset.min, 10) || 0;
    const max = parseInt(input.dataset.max, 10) || Infinity;
    const tooShort = (input.required || value.length > 0) && value.length < Math.max(min, input.required ? 1 : 0);
    const tooLong = value.length > max;
    return tooShort || tooLong ? input.dataset.error || `Please enter ${min} to ${max} characters.` : '';
  };

  const validate = (input, showError) => {
    const msg = message(input);
    const { error, counter } = parts(input);
    input.setCustomValidity(msg);

    if (counter) counter.textContent = `${input.value.length}/${input.dataset.max}`;

    const paymentButton = paymentButtonFor(input);
    if (paymentButton) paymentButton.classList.toggle('nai-personalization-locked', Boolean(msg));

    if (showError || (error && !error.hidden)) {
      if (error) {
        error.textContent = msg;
        error.hidden = !msg;
      }
      if (msg) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }
    return !msg;
  };

  const showInvalid = (input) => {
    validate(input, true);
    input.focus({ preventScroll: true });
    input.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const initAll = () => document.querySelectorAll(SELECTOR).forEach((input) => validate(input, false));

  document.addEventListener('input', (event) => {
    if (event.target.matches && event.target.matches(SELECTOR)) validate(event.target, false);
  });

  document.addEventListener(
    'focusout',
    (event) => {
      const input = event.target;
      if (!input.matches || !input.matches(SELECTOR)) return;
      const trimmed = input.value.trim();
      if (trimmed !== input.value) input.value = trimmed;
      if (input.value.length > 0) validate(input, true);
    },
    true
  );

  /* Add to cart: block the product form submit while the field is invalid. */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || inputsForForm(form).length === 0) return;
      if (!validateForm(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /* Browser validation bubble (if a form validates natively): show our message instead. */
  document.addEventListener(
    'invalid',
    (event) => {
      const input = event.target;
      if (!input.matches || !input.matches(SELECTOR)) return;
      event.preventDefault();
      showInvalid(input);
    },
    true
  );

  /* Buy it now: block the click while the field is invalid. */
  document.addEventListener(
    'click',
    (event) => {
      const paymentButton = event.target.closest && event.target.closest('.shopify-payment-button');
      if (!paymentButton) return;
      const productForm = paymentButton.closest('product-form');
      const form = productForm && productForm.querySelector('form');
      if (!form) return;
      if (!validateForm(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /* Dawn may re-render parts of the form on variant change: re-apply the Buy it now lock. */
  const subscribeVariantChange = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(initAll, 0));
    }
  };

  const start = () => {
    initAll();
    subscribeVariantChange();
    /* The dynamic checkout button is injected by Shopify after load. */
    window.addEventListener('load', initAll);
    setTimeout(initAll, 1500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
